import { Bookmarks, CheckCircleSolid, Plus, ShoppingCart, Spinner, XMark } from "@medusajs/icons"
import { Button, Text, clx, toast } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState, type ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { useCosts, withChinaShipping } from "../lib/cost-settings"
import { itemDescription, photoSrc, type AlbumDetail } from "../lib/finder"
import { finderApi, type FinderPickRow } from "../lib/finder-api"
import { ago } from "../lib/format"
import { caps, t } from "../lib/i18n"
import { errorMessage } from "../lib/medusa"
import { addToOpenOrder, foundItem, useOpenOrders } from "../lib/workbench"

/**
 * A product found at a supplier, ready to go into a custom order or onto the quick saves list.
 * `yuan` is the supplier's price; the order line gets it plus the shipping inside China.
 */
export type Found = { title: string; link: string; yuan: number | null; image: string | null; album_id?: string | null; source: "album" | "link"; en: string; zh?: string | null; pick_id?: string | null }

/** An album from the finder as a found product: its shop link (else the album itself), price, first photo. */
export function albumFound(data: AlbumDetail): Found {
  const a = data.album
  const category = a.categories.find((c) => !c.hidden)?.name ?? null
  const cover = data.photos.find((p) => p.is_cover) ?? data.photos[0]
  const handle = data.supplier?.handle ?? ""
  return {
    title: a.title,
    link: a.buy_link?.url ?? a.yupoo_url ?? "",
    yuan: a.price_yuan,
    image: cover ? photoSrc({ hash: cover.hash, handle }, "small") : null,
    album_id: a.id,
    source: "album",
    en: itemDescription({ title: a.title, code: a.code }, category),
  }
}

/** A saved item back as a found product (it then leaves the list when the order is saved). */
export const pickFound = (p: FinderPickRow): Found => ({ title: p.title, link: p.link, yuan: p.yuan, image: p.image, album_id: p.album_id, source: p.source, en: p.en ?? p.title, zh: p.zh, pick_id: p.id })

/** A panel from the bottom on a phone, centred on a desktop. */
export function BottomSheet({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => event.key === "Escape" && onClose()
    window.addEventListener("keydown", key)
    return () => window.removeEventListener("keydown", key)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="bg-ui-bg-base pb-safe flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-2xl shadow-xl sm:max-w-md sm:rounded-2xl"
      >
        <div className="border-ui-border-base flex items-center justify-between gap-x-3 border-b px-4 py-3">
          <div className="txt-compact-medium-plus min-w-0 truncate">{title}</div>
          <button type="button" onClick={onClose} aria-label={t("Close")} className="text-ui-fg-muted hover:text-ui-fg-base -mr-1 rounded p-1.5">
            <XMark />
          </button>
        </div>
        <div className="overflow-y-auto p-3">{children}</div>
      </div>
    </div>
  )
}

const Row = ({ onClick, icon, title, sub, disabled }: { onClick: () => void; icon: ReactNode; title: ReactNode; sub?: ReactNode; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="hover:bg-ui-bg-base-hover active:bg-ui-bg-base-pressed flex w-full items-center gap-x-3 rounded-lg px-2.5 py-2.5 text-left disabled:opacity-50"
  >
    <span className="bg-ui-bg-subtle text-ui-fg-subtle flex size-9 shrink-0 items-center justify-center rounded-lg">{icon}</span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="txt-compact-small-plus truncate">{title}</span>
      {sub && <span className="txt-compact-xsmall text-ui-fg-muted truncate">{sub}</span>}
    </span>
  </button>
)

/**
 * «Add to an order» (owners, 16 Oct 2026): the product goes into one of the custom orders being filled
 * on this phone, or a new one, or onto the quick saves list, and the screen stays where it is so the
 * search can go on. `resolve` gets the product first when the button only has a summary (a search tile).
 */
export function AddToOrder({ found, resolve, compact = false, className }: { found?: Found; resolve?: () => Promise<Found>; compact?: boolean; className?: string }) {
  const [open, setOpen] = useState(false)
  const [item, setItem] = useState<Found | null>(found ?? null)
  const [loading, setLoading] = useState(false)
  useEffect(() => setItem(found ?? null), [found])
  const start = async () => {
    if (item || !resolve) return setOpen(true)
    setLoading(true)
    try {
      setItem(await resolve())
      setOpen(true)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setLoading(false)
    }
  }
  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            void start()
          }}
          aria-label={t("Add to an order")}
          className={clx("bg-ui-fg-base text-ui-bg-base flex size-9 items-center justify-center rounded-full shadow-md", className)}
        >
          {loading ? <Spinner className="animate-spin" /> : <Plus />}
        </button>
      ) : (
        <Button size="small" className={className} isLoading={loading} onClick={() => void start()}>
          <ShoppingCart /> {t("Add to an order")}
        </Button>
      )}
      {open && item && <AddSheet found={item} onClose={() => setOpen(false)} />}
    </>
  )
}

function AddSheet({ found, onClose }: { found: Found; onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const costs = useCosts()
  const orders = useOpenOrders(t("New order"))
  const [done, setDone] = useState<{ kind: "order"; id: string; label: string } | { kind: "saved"; existing: boolean } | null>(null)
  const cost = found.yuan != null ? withChinaShipping(found.yuan, costs) : null
  const line = () => foundItem({ en: found.en, zh: found.zh, link: found.link, cost_yuan: cost, pick_id: found.pick_id })
  const put = (id: string | null, label: string) => setDone({ kind: "order", id: addToOpenOrder(id, line()), label })
  const save = useMutation({
    mutationFn: () => finderApi.savePick({ title: found.title, link: found.link, yuan: found.yuan, image: found.image, album_id: found.album_id ?? null, source: found.source, en: found.en, zh: found.zh ?? null }),
    onSuccess: (answer) => {
      void queryClient.invalidateQueries({ queryKey: ["hq", "finder", "picks"] })
      setDone({ kind: "saved", existing: answer.existing })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  return (
    <BottomSheet title={t("Add to an order")} onClose={onClose}>
      <div className="flex items-center gap-x-3 px-1 pb-3">
        {found.image ? <img src={found.image} alt="" className="bg-ui-bg-subtle size-14 shrink-0 rounded-md object-cover" /> : <span className="bg-ui-bg-subtle size-14 shrink-0 rounded-md" />}
        <div className="flex min-w-0 flex-col gap-y-0.5">
          <span className="txt-compact-small-plus line-clamp-2 break-words">{found.en || found.title}</span>
          {found.zh && <span className="hc-data text-ui-fg-muted truncate text-[11.5px]">{found.zh}</span>}
          {cost != null && <span className="hc-data text-ui-fg-subtle text-[12px]">{t("Cost ¥{cost} with shipping in China", { cost })}</span>}
        </div>
      </div>

      {done ? (
        <div className="flex flex-col items-center gap-y-3 px-2 py-4 text-center">
          <CheckCircleSolid className="text-ui-tag-green-icon size-8" />
          <Text weight="plus">{done.kind === "order" ? t("Added to «{order}»", { order: done.label }) : done.existing ? t("Already on your saved list") : t("Saved for later")}</Text>
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            {done.kind === "order" && (
              <Button className="flex-1" onClick={() => navigate(`/custom/new?w=${encodeURIComponent(done.id)}`)}>
                {t("Open the order")}
              </Button>
            )}
            {done.kind === "saved" && (
              <Button className="flex-1" variant="secondary" onClick={() => navigate("/finder/saved")}>
                {t("See the saved list")}
              </Button>
            )}
            <Button className="flex-1" variant="secondary" onClick={onClose}>
              {t("Keep searching")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {orders.length > 0 && <p className="hc-eyebrow px-2.5 pb-1">{caps(t("Orders you are filling"))}</p>}
          {orders.map((order) => (
            <Row
              key={order.id}
              onClick={() => put(order.id, order.label)}
              icon={<ShoppingCart />}
              title={order.label}
              sub={`${t.n(order.items, "{n} item", "{n} items")} · ${ago(new Date(order.saved_at).toISOString())}`}
            />
          ))}
          <Row onClick={() => put(null, t("New order"))} icon={<Plus />} title={t("New custom order")} sub={t("Starts a new order with this item")} />
          {!found.pick_id && (
            <>
              <div className="border-ui-border-base my-2 border-t" />
              <Row onClick={() => save.mutate()} disabled={save.isPending} icon={<Bookmarks />} title={t("Save for later")} sub={t("On your saved list until an order uses it")} />
            </>
          )}
        </div>
      )}
    </BottomSheet>
  )
}

/** «Save for later» on its own, one tap (the album page and the product page). */
export function SaveForLater({ found }: { found: Found }) {
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: () => finderApi.savePick({ title: found.title, link: found.link, yuan: found.yuan, image: found.image, album_id: found.album_id ?? null, source: found.source, en: found.en, zh: found.zh ?? null }),
    onSuccess: (answer) => {
      void queryClient.invalidateQueries({ queryKey: ["hq", "finder", "picks"] })
      toast.success(answer.existing ? t("Already on your saved list") : t("Saved for later"))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  return (
    <Button size="small" variant="secondary" isLoading={save.isPending} disabled={!found.link} onClick={() => save.mutate()}>
      <Bookmarks /> {t("Save for later")}
    </Button>
  )
}
